import { useMemo } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import BodyText from 'LuxKit/Typography/BodyText'
import Caption from 'LuxKit/Typography/Caption'
import Heading from 'LuxKit/Typography/Heading'
import FormatCurrency from 'components/Common/FormatCurrency'
import Group from 'components/utils/Group'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import LuxPlusLogoWrapper from 'luxPlus/components/LuxPlusLogoWrapper'
import useLuxPlusDisplayedMode from 'luxPlus/hooks/useLuxPlusDisplayedMode'
import { formatSaleUnitLabel } from 'hooks/useSaleUnitFormatting'

const PriceContainer = styled.span`
  display: inline;
`

const BurnPointsValue = styled.span`
  color: ${props => props.theme.palette.brand.primary.normal};
`

/**
 * Displays a loyalty points burn price, showing how many points the user will spend
 * alongside any remaining cash balance. Handles both standard and LuxPlus member pricing.
 */
interface Props {
  /** The full cash price of the offer before any points are applied */
  totalPrice: number
  /** The number of points the user has chosen to burn */
  pointsToBurn?: number
  /** The full points value of the offer (i.e. the price expressed entirely in points) */
  priceInPoints?: number
  size?: 'S' | 'M' | 'L'
  saleUnit?: 'total' | 'night' | 'person'
  memberPrice?: number
  /** When true, hides the cash component if it would be zero (points cover the full price) */
  hideZeroCash?: boolean
  offerConfig?: App.OfferLuxPlusConfig
  isTour?: boolean
  mode?: App.LuxPlusPriceStackMode
}

function LoyaltyBurnPriceDisplay(props: Props) {
  const {
    totalPrice,
    pointsToBurn = 0,
    priceInPoints = 0,
    size = 'L',
    saleUnit = 'total',
    memberPrice,
    hideZeroCash = false,
    offerConfig,
    isTour,
    mode,
  } = props

  const intl = useIntl()
  const displayedMode = useLuxPlusDisplayedMode({
    price: totalPrice,
    memberPrice: memberPrice ?? 0,
    offerConfig,
    isTour,
    mode,
  })

  const resolvedMemberPrice = memberPrice && memberPrice > 0 ? memberPrice : totalPrice
  const pointsCashValue = priceInPoints > 0 ? (pointsToBurn * totalPrice) / priceInPoints : 0
  const remainingCash = pointsToBurn >= priceInPoints ? 0 : Math.ceil(Math.max(0, totalPrice - pointsCashValue))
  const memberPointsCashValue = priceInPoints > 0 ? (pointsToBurn * resolvedMemberPrice) / priceInPoints : 0
  const memberRemainingCash = pointsToBurn >= priceInPoints ? 0 : Math.ceil(Math.max(0, resolvedMemberPrice - memberPointsCashValue))
  const effectivePointsBurned = Math.min(pointsToBurn, priceInPoints)

  const saleUnitLabel = formatSaleUnitLabel(intl, saleUnit)

  const showCash = hideZeroCash ? remainingCash > 0 : true
  const showMemberCash = hideZeroCash ? memberRemainingCash > 0 : true

  const nonMemberBurnPriceLabel = useMemo(() => {
    if (hideZeroCash && !showCash) {
      return intl.formatMessage(
        {
          id: 'lRU1m5sm7wOxouX_aYnJ',
          defaultMessage: 'Non-member {points}',
          description: 'Price caption label for non-member points-only display',
        },
        {
          points: <b><FormatLoyaltyPoints points={effectivePointsBurned} abbreviated /></b>,
        },
      )
    }
    return intl.formatMessage(
      {
        id: 'PIwh0lBFZFNGTPKTnNh_',
        defaultMessage: 'Non-member {points} + {price}',
        description: 'Price caption label for non-member points + price display',
      },
      {
        points: <b><FormatLoyaltyPoints points={effectivePointsBurned} abbreviated /></b>,
        price: <b><FormatCurrency value={remainingCash} format="roundedDollar" /></b>,
      },
    )
  }, [intl, effectivePointsBurned, remainingCash, showCash, hideZeroCash])

  if (props.pointsToBurn === undefined || props.priceInPoints === undefined) return null

  const headingVariant = size === 'L' ? 'heading4' : size === 'M' ? 'heading5' : 'heading6'

  const leadWithMemberPrice = displayedMode === 'member-prominent' || displayedMode === 'regular-prominent'
  const isMemberOnly = displayedMode === 'member-only' || displayedMode === 'member-checkout-total' || displayedMode === 'member-compact'

  if (leadWithMemberPrice || isMemberOnly) {
    return (
      <Group direction="vertical" gap={2} data-testid="burn-price-display-luxplus">
        <PriceContainer>
          <Heading variant={headingVariant} as="span" colour="lux-plus">
            <FormatLoyaltyPoints points={effectivePointsBurned} abbreviated />
            {showMemberCash && (
              <>
                {' + '}
                <FormatCurrency value={memberRemainingCash} format="roundedDollar" />
              </>
            )}
          </Heading>
          {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
          {' '}
          <Caption variant="medium" as="span" colour="lux-plus">
            <FormattedMessage
              id="Bqt2LSkQYirkR4WS1QYh"
              defaultMessage="{saleUnit} with {luxPlusLogo}"
              description="Sale unit label with LuxPlus branding for points price"
              values={{
                saleUnit: saleUnitLabel,
                luxPlusLogo: <LuxPlusLogoWrapper height={8} />,
              }}
            />
          </Caption>
        </PriceContainer>
        {leadWithMemberPrice && resolvedMemberPrice < totalPrice && <Caption variant="medium" colour="light-foreground-alt">
          {nonMemberBurnPriceLabel}
        </Caption>}
      </Group>
    )
  }

  let priceContent: React.ReactNode
  if (size === 'S') {
    priceContent = <BodyText variant="medium" weight="bold" as="span">
      <BurnPointsValue>
        <FormatLoyaltyPoints points={effectivePointsBurned} abbreviated />
      </BurnPointsValue>
      {showCash && <>{' + '}<FormatCurrency value={remainingCash} format="roundedDollar" /></>}
    </BodyText>
  } else {
    priceContent = <Heading variant={headingVariant} as="span">
      <BurnPointsValue>
        <FormatLoyaltyPoints points={effectivePointsBurned} abbreviated />
      </BurnPointsValue>
      {showCash && <>{' + '}<FormatCurrency value={remainingCash} format="roundedDollar" /></>}
    </Heading>
  }

  return (
    <Group direction="vertical" gap={2}>
      <PriceContainer data-testid="burn-price-display">
        {priceContent}
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {' '}
        <Caption variant="medium" as="span" colour="light-foreground-alt" weight="normal">
          {saleUnitLabel}
        </Caption>
      </PriceContainer>
    </Group>
  )
}

export default LoyaltyBurnPriceDisplay
